import { useEffect, useState } from "react";
import api from "../lib/api";
import { Camera, X, Loader2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Thumbnail row + click-to-enlarge lightbox for work-order photos.
 * Auth-scoped: both investor (on their WOs) and the submitting tenant see
 * the same photos via /api/maintenance/{wid}/photos.
 *
 * Props:
 *  - workOrderId: string (required)
 *  - size: "sm" (40px) | "md" (64px) — thumbnail tile size, default sm
 *  - maxPreview: number of thumbs shown before "+N more" chip (default 4)
 *  - onEmpty: optional ReactNode when no photos exist
 */
export default function WorkOrderPhotos({ workOrderId, size = "sm", maxPreview = 4, onEmpty = null }) {
  const [items, setItems] = useState(null);
  const [lightbox, setLightbox] = useState(null); // photo object or null
  const sizePx = size === "md" ? 64 : 40;

  useEffect(() => {
    let cancelled = false;
    api
      .get(`/maintenance/${workOrderId}/photos`)
      .then((r) => {
        if (!cancelled) setItems(r.data.items || []);
      })
      .catch(() => {
        if (!cancelled) setItems([]);
      });
    return () => {
      cancelled = true;
    };
  }, [workOrderId]);

  if (items === null) {
    return (
      <div className="inline-flex items-center gap-1 text-slate-400 text-xs">
        <Loader2 className="w-3 h-3 animate-spin" /> photos…
      </div>
    );
  }
  if (items.length === 0) return onEmpty;

  const shown = items.slice(0, maxPreview);
  const hiddenCount = items.length - shown.length;

  const getAuth = () => localStorage.getItem("rv_token");
  const thumbUrl = (p) =>
    `${API}/api/maintenance/${workOrderId}/photos/${p.id}/download?_=${encodeURIComponent(getAuth() || "")}`;

  return (
    <>
      <div className="inline-flex items-center gap-1.5 flex-wrap" data-testid={`wo-photos-${workOrderId}`}>
        {shown.map((p) => (
          <button
            key={p.id}
            type="button"
            onClick={() => setLightbox(p)}
            className="rounded-sm overflow-hidden border border-slate-200 hover:border-slate-900 transition-colors shrink-0 bg-slate-100"
            style={{ width: sizePx, height: sizePx }}
            data-testid={`wo-photo-thumb-${p.id}`}
            title={p.caption || p.original_filename}
          >
            <AuthedImg
              path={`/maintenance/${workOrderId}/photos/${p.id}/download`}
              alt={p.caption || p.original_filename}
              className="w-full h-full object-cover"
            />
          </button>
        ))}
        {hiddenCount > 0 && (
          <button
            type="button"
            onClick={() => setLightbox(items[shown.length])}
            className="rounded-sm border border-slate-200 text-xs font-semibold text-slate-600 hover:bg-slate-100 shrink-0 flex items-center justify-center"
            style={{ width: sizePx, height: sizePx }}
            data-testid={`wo-photos-more-${workOrderId}`}
          >
            +{hiddenCount}
          </button>
        )}
      </div>

      {lightbox && (
        <Lightbox
          photos={items}
          startId={lightbox.id}
          workOrderId={workOrderId}
          onClose={() => setLightbox(null)}
        />
      )}
    </>
  );
}

/**
 * Compact badge alternative: just a "📷 N" count. Click opens the lightbox.
 */
export function WorkOrderPhotoBadge({ workOrderId, count: initialCount }) {
  const [count, setCount] = useState(initialCount);
  const [items, setItems] = useState(null);
  const [lightbox, setLightbox] = useState(false);

  useEffect(() => {
    if (typeof initialCount === "number") return;
    api.get(`/maintenance/${workOrderId}/photos`).then((r) => {
      setItems(r.data.items || []);
      setCount(r.data.items?.length || 0);
    }).catch(() => setCount(0));
  }, [workOrderId, initialCount]);

  if (count === 0 || count == null) return null;

  const open = async () => {
    if (!items) {
      const r = await api.get(`/maintenance/${workOrderId}/photos`);
      setItems(r.data.items || []);
    }
    setLightbox(true);
  };

  return (
    <>
      <button
        type="button"
        onClick={open}
        className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest border border-slate-300 bg-white text-slate-700 hover:bg-slate-900 hover:text-white px-1.5 py-0.5 rounded-sm transition-colors"
        data-testid={`wo-photo-badge-${workOrderId}`}
      >
        <Camera className="w-3 h-3" /> {count}
      </button>
      {lightbox && items && items.length > 0 && (
        <Lightbox photos={items} startId={items[0].id} workOrderId={workOrderId} onClose={() => setLightbox(false)} />
      )}
    </>
  );
}

/**
 * Renders an <img> whose src comes from an authenticated fetch → blob URL.
 * Object storage responses require the bearer token, which browsers don't
 * attach to a plain <img src=...> request, so we fetch-then-blob.
 */
function AuthedImg({ path, alt, className }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let revoke = null;
    const token = localStorage.getItem("rv_token");
    fetch(`${API}/api${path}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      // credentials: "omit" — same iOS Safari / CORS rationale as
      // /app/frontend/src/lib/api.js and /app/frontend/src/lib/pdf.js.
      credentials: "omit",
    })
      .then((r) => (r.ok ? r.blob() : Promise.reject(r)))
      .then((b) => {
        const u = URL.createObjectURL(b);
        revoke = u;
        setSrc(u);
      })
      .catch(() => setSrc(null));
    return () => {
      if (revoke) URL.revokeObjectURL(revoke);
    };
  }, [path]);
  if (!src) return <div className={className + " animate-pulse bg-slate-200"} />;
  return <img src={src} alt={alt} className={className} loading="lazy" />;
}

function Lightbox({ photos, startId, workOrderId, onClose }) {
  const startIdx = Math.max(0, photos.findIndex((p) => p.id === startId));
  const [idx, setIdx] = useState(startIdx);
  const p = photos[idx];
  if (!p) return null;

  const prev = (e) => { e?.stopPropagation(); setIdx((i) => (i - 1 + photos.length) % photos.length); };
  const next = (e) => { e?.stopPropagation(); setIdx((i) => (i + 1) % photos.length); };

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="wo-photo-lightbox"
      role="dialog"
      aria-modal="true"
    >
      <button
        type="button"
        onClick={onClose}
        className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
        aria-label="Close"
      >
        <X className="w-5 h-5" />
      </button>
      <div className="max-w-5xl w-full max-h-[90vh] flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
        <AuthedImg
          path={`/maintenance/${workOrderId}/photos/${p.id}/download`}
          alt={p.caption || p.original_filename}
          className="max-h-[75vh] max-w-full object-contain rounded-sm"
        />
        <div className="mt-4 flex items-center gap-4 text-white/80 text-xs font-mono">
          <button onClick={prev} className="hover:text-white" data-testid="wo-photo-prev">← Prev</button>
          <span>{idx + 1} / {photos.length}</span>
          <button onClick={next} className="hover:text-white" data-testid="wo-photo-next">Next →</button>
        </div>
        {p.caption && <div className="mt-2 text-white/60 text-sm text-center max-w-xl">{p.caption}</div>}
        <div className="mt-1 text-white/40 text-[10px] font-mono">
          {p.uploaded_by_role === "tenant" ? "Uploaded by tenant" : "Uploaded by investor"} · {p.original_filename}
        </div>
      </div>
    </div>
  );
}
